import { Capability, CapabilityMode, Company, LineOfBusiness, TreeInfo, TreeMember } from '../models/domain.models';
import { SessionUser } from '../session/session.models';

export const TREE_RIOS = 'tree-rios';
export const TREE_FEDERAL = 'tree-federal';

export const TREES: TreeInfo[] = [
  { oid: TREE_RIOS, name: 'Ríos Seguros', rootOid: 'mem-martin', plan: 'Pro', paymentStatus: 'up-to-date' },
  { oid: TREE_FEDERAL, name: 'Grupo Federal Brokers', rootOid: 'mem-fed-ferro', plan: 'Básico', paymentStatus: 'up-to-date' },
];

export const TREE = TREES[0];

export const MEMBER = {
  martin: 'mem-martin',
  laura: 'mem-laura',
  diego: 'mem-diego',
  valentina: 'mem-valentina',
  gestion: 'mem-gestion',
  sofia: 'mem-sofia',
  tomas: 'mem-tomas',
} as const;

type SeedMember = Omit<TreeMember, 'statusHistory' | 'inOtherTrees' | 'ownCodes' | 'invitationOid' | 'treeOid'>;

const SEED_MEMBERS: SeedMember[] = [
  {
    oid: MEMBER.martin, kind: 'producer', name: 'Martín Ríos', legalForm: 'natural', parentOid: null,
    status: 'active', canEmit: true, license: 'MAT-48213', taxId: '20-18234567-4',
    email: 'martin.rios@riosseguros.example', phone: '11 5555-0101',
  },
  {
    oid: MEMBER.laura, kind: 'producer', name: 'Laura Gómez', legalForm: 'natural', parentOid: MEMBER.martin,
    status: 'active', canEmit: true, license: 'MAT-51877', taxId: '27-22345678-3',
    email: 'laura.gomez@riosseguros.example', phone: '11 5555-0102',
  },
  {
    oid: MEMBER.diego, kind: 'producer', name: 'Diego Paz', legalForm: 'natural', parentOid: MEMBER.laura,
    status: 'suspended', canEmit: true, license: 'MAT-60112', taxId: '20-30456789-1',
    email: 'diego.paz@riosseguros.example', phone: '11 5555-0103',
  },
  {
    oid: MEMBER.valentina, kind: 'seller', name: 'Valentina Sosa', legalForm: 'natural', parentOid: MEMBER.laura,
    status: 'active', canEmit: false, license: null, taxId: null,
    email: 'valentina.sosa@riosseguros.example', phone: '11 5555-0104',
  },
  {
    oid: MEMBER.gestion, kind: 'producer', name: 'Gestión Integral SRL', legalForm: 'legal', parentOid: MEMBER.martin,
    status: 'active', canEmit: true, license: 'MAT-70420', taxId: '30-71234567-8',
    email: 'contacto@gestionintegral.example', phone: '11 5555-0105',
  },
  {
    oid: MEMBER.sofia, kind: 'seller', name: 'Sofía Ledesma', legalForm: 'natural', parentOid: MEMBER.martin,
    status: 'active', canEmit: false, license: null, taxId: null,
    email: 'sofia.ledesma@riosseguros.example', phone: '11 5555-0106',
  },
  {
    oid: MEMBER.tomas, kind: 'seller', name: 'Tomás Vera', legalForm: 'natural', parentOid: MEMBER.martin,
    status: 'active', canEmit: true, license: null, taxId: null,
    email: 'tomas.vera@riosseguros.example', phone: '11 5555-0107',
  },
];

export const MEMBERS: TreeMember[] = SEED_MEMBERS.map(m => ({
  ...m,
  treeOid: TREE_RIOS,
  statusHistory: [],
  inOtherTrees: m.oid === MEMBER.martin,
  ownCodes: (m.oid === MEMBER.tomas ? { 'cia-plata': 'TV-0457' } : {}) as Record<string, string>,
  invitationOid: null,
}));

export const MEMBER_FED = {
  ferro: 'mem-fed-ferro',
  martin: 'mem-fed-martin',
  claudia: 'mem-fed-claudia',
  pablo: 'mem-fed-pablo',
} as const;

const SEED_MEMBERS_FED: SeedMember[] = [
  {
    oid: MEMBER_FED.ferro, kind: 'producer', name: 'Andrés Ferro', legalForm: 'natural', parentOid: null,
    status: 'active', canEmit: true, license: 'MAT-39004', taxId: '20-16789012-5',
    email: 'andres.ferro@federalbrokers.example', phone: '11 5555-0201',
  },
  {
    oid: MEMBER_FED.martin, kind: 'producer', name: 'Martín Ríos', legalForm: 'natural', parentOid: MEMBER_FED.ferro,
    status: 'active', canEmit: true, license: 'MAT-48213', taxId: '20-18234567-4',
    email: 'martin.rios@riosseguros.example', phone: '11 5555-0101',
  },
  {
    oid: MEMBER_FED.claudia, kind: 'producer', name: 'Claudia Roldán', legalForm: 'natural', parentOid: MEMBER_FED.ferro,
    status: 'suspended', canEmit: true, license: 'MAT-57731', taxId: '27-24567891-6',
    email: 'claudia.roldan@federalbrokers.example', phone: '11 5555-0202',
  },
  {
    oid: MEMBER_FED.pablo, kind: 'seller', name: 'Pablo Ibarra', legalForm: 'natural', parentOid: MEMBER_FED.martin,
    status: 'active', canEmit: false, license: null, taxId: null,
    email: 'pablo.ibarra@federalbrokers.example', phone: '11 5555-0203',
  },
];

export const MEMBERS_FED: TreeMember[] = SEED_MEMBERS_FED.map(m => ({
  ...m,
  treeOid: TREE_FEDERAL,
  statusHistory: [],
  inOtherTrees: m.oid === MEMBER_FED.martin,
  ownCodes: {} as Record<string, string>,
  invitationOid: null,
}));

/** Which node of each tree a demo username operates as; only `prod` belongs to two trees. */
export const MEMBERSHIPS: Record<string, { treeOid: string; memberOid: string }[]> = {
  prod: [
    { treeOid: TREE_RIOS, memberOid: MEMBER.martin },
    { treeOid: TREE_FEDERAL, memberOid: MEMBER_FED.martin },
  ],
  sales: [{ treeOid: TREE_RIOS, memberOid: MEMBER.sofia }],
};

export const DEMO_USERS: SessionUser[] = [
  {
    oid: 'usr-prod', username: 'prod', nombre: 'Martín', apellido: 'Ríos', email: 'martin.rios@riosseguros.example',
    role: 'producer', canEmit: true, memberOid: MEMBER.martin, treeOid: TREE_RIOS, treeName: TREE.name,
    treeCount: 2, treePending: false, support: null,
  },
  {
    oid: 'usr-sales', username: 'sales', nombre: 'Sofía', apellido: 'Ledesma', email: 'sofia.ledesma@riosseguros.example',
    role: 'seller', canEmit: false, memberOid: MEMBER.sofia, treeOid: TREE_RIOS, treeName: TREE.name,
    treeCount: 1, treePending: false, support: null,
  },
  {
    oid: 'usr-admin', username: 'admin', nombre: 'Soporte', apellido: 'Plataforma', email: 'soporte@plataforma.example',
    role: 'admin', canEmit: false, memberOid: null, treeOid: null, treeName: null,
    treeCount: 0, treePending: false, support: null,
  },
];

const ALL_LINES: LineOfBusiness[] = ['auto', 'home', 'commerce', 'liability', 'personal-accident', 'life'];

function caps(
  quote: CapabilityMode, issue: CapabilityMode, endorse: CapabilityMode, reportClaim: CapabilityMode,
  queryClaims: CapabilityMode, queryPolicies: CapabilityMode, documents: CapabilityMode,
  collections: CapabilityMode, settlements: CapabilityMode,
): Record<Capability, CapabilityMode> {
  return { quote, issue, endorse, reportClaim, queryClaims, queryPolicies, documents, collections, settlements };
}

export const COMPANY = {
  plata: 'cia-plata',
  austral: 'cia-austral',
  federal: 'cia-federal',
  nexo: 'cia-nexo',
  pampa: 'cia-pampa',
  litoral: 'cia-litoral',
} as const;

export const COMPANIES: Company[] = [
  {
    oid: COMPANY.plata, name: 'Aseguradora del Plata', lines: ALL_LINES,
    capabilities: caps('api', 'api', 'api', 'api', 'api', 'api', 'api', 'file', 'file'),
    quoteDelayMs: [1000, 3000], simulatedBehaviour: 'Rápida y estable',
  },
  {
    oid: COMPANY.austral, name: 'La Austral Seguros', lines: ALL_LINES,
    capabilities: caps('api', 'api', 'manual', 'manual', 'api', 'api', 'api', 'file', 'file'),
    quoteDelayMs: [8000, 15000], simulatedBehaviour: 'Lenta (8 a 15 s para cotizar)',
  },
  {
    oid: COMPANY.federal, name: 'Federal Andina', lines: ALL_LINES,
    capabilities: caps('api', 'api', 'manual', 'manual', 'manual', 'api', 'manual', 'file', 'manual'),
    quoteDelayMs: [3000, 6000], simulatedBehaviour: 'A veces da timeout',
  },
  {
    oid: COMPANY.nexo, name: 'Nexo Seguros', lines: ALL_LINES,
    capabilities: caps('api', 'manual', 'manual', 'manual', 'manual', 'manual', 'manual', 'manual', 'file'),
    quoteDelayMs: [2000, 5000], simulatedBehaviour: 'Para algunos riesgos responde "sin oferta"',
  },
  {
    oid: COMPANY.pampa, name: 'Pampa Mutual', lines: ALL_LINES,
    capabilities: caps('api', 'api', 'api', 'manual', 'manual', 'api', 'api', 'api', 'file'),
    quoteDelayMs: [2000, 6000], simulatedBehaviour: 'Error de credenciales en un productor hijo',
  },
  {
    oid: COMPANY.litoral, name: 'Litoral Vida', lines: ['life', 'personal-accident'],
    capabilities: caps('api', 'api', 'manual', 'manual', 'manual', 'api', 'api', 'file', 'file'),
    quoteDelayMs: [1000, 4000], simulatedBehaviour: 'Solo opera Vida enlatado y Accidentes Personales',
  },
];
